import React from "react";
import { AxiosRequestConfig } from "axios";
import { GranularCatalogConfig } from "@granular/fabric-catalog-helpers";
import { UnreadReportsCountResponse } from "../api/models";
import { getUnreadReportsCount } from "../api/reports";

// interval at which to poll for unreadReportsCount
export const STANDARD_INTERVAL = 30; // seconds

type UseGetUnreadReportsCountArgs = {
  enabled: boolean;
  pollingIntervalSeconds: number;
};

const defaultArgs: UseGetUnreadReportsCountArgs = {
  enabled: true,
  pollingIntervalSeconds: STANDARD_INTERVAL,
};

export const useGetUnreadReportsCount = (
  catalogConfig: GranularCatalogConfig,
  pathname: string,
  ownRequestConfig?: AxiosRequestConfig,
  args?: Partial<UseGetUnreadReportsCountArgs>
): number => {
  const fullArgs = args ? { ...defaultArgs, ...args } : defaultArgs;

  const [count, setCount] = React.useState(0);
  const intervalRef = React.useRef<NodeJS.Timer>();

  const fetchCount = React.useCallback(() => {
    // Don't fetch if the browser tab is not active or if the hook was told to be disabled
    if (document.hidden || !fullArgs.enabled) {
      return;
    }
    getUnreadReportsCount(catalogConfig, ownRequestConfig)
      .then((resp: UnreadReportsCountResponse) => {
        setCount(resp.data.count);
      })
      .catch((error: Error) => {
        console.log(error);
      });
  }, [catalogConfig, fullArgs.enabled, ownRequestConfig]);

  const stopInterval = () => {
    if (intervalRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: No overload matches this call
      clearInterval(intervalRef.current);
    }
  };

  const resetInterval = React.useCallback(() => {
    stopInterval();
    fetchCount();
    intervalRef.current = setInterval(
      fetchCount,
      fullArgs.pollingIntervalSeconds * 1000
    );
  }, [fetchCount, fullArgs.pollingIntervalSeconds]);

  React.useEffect(() => {
    // When the reports app is active, force a refresh in 5 seconds
    // which should return after any unread reports have been marked
    // as viewed
    if (pathname.startsWith("/reports")) {
      setTimeout(fetchCount, 5 * 1000);
    }
  }, [fetchCount, pathname]);

  React.useEffect(() => {
    resetInterval();

    // When the browser tab is selected, fetch report count
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetInterval();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // On dismount, remove interval and browser tab listener
    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [resetInterval]);

  return count;
};
